import { apiFetch } from '../services/cloudflareService';
import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { 
    Book, 
    Code, 
    Key, 
    Link as LinkIcon, 
    ShieldAlert, 
    Clock, 
    Menu, 
    X, 
    Loader2,
    CheckCircle2,
    Repeat,
    Plug
} from 'lucide-react';

// Sections (we will extract these to separate components)
import OverviewSection from '../components/dev-portal/OverviewSection';
import ApiSection from '../components/dev-portal/ApiSection';
import AuthSection from '../components/dev-portal/AuthSection';
import FhirSection from '../components/dev-portal/FhirSection';
import SecuritySection from '../components/dev-portal/SecuritySection';
import ChangelogSection from '../components/dev-portal/ChangelogSection';
import WorkflowSection from '../components/dev-portal/WorkflowSection';
import TasyGuideSection from '../components/dev-portal/TasyGuideSection';

type SectionId = 'overview' | 'api' | 'auth' | 'workflow' | 'tasy' | 'fhir' | 'security' | 'changelog';
export type Language = 'en' | 'es';

const SECTIONS: { id: SectionId; labelEn: string; labelEs: string; icon: React.ElementType }[] = [
    { id: 'overview', labelEn: 'Overview', labelEs: 'Visión General', icon: Book },
    { id: 'api', labelEn: 'API Reference', labelEs: 'Referencia API', icon: Code },
    { id: 'auth', labelEn: 'Authentication', labelEs: 'Autenticación', icon: Key },
    { id: 'workflow', labelEn: 'Integration Workflow', labelEs: 'Flujo de Integración', icon: Repeat },
    { id: 'tasy', labelEn: 'TASY EMR Guide', labelEs: 'Guía TASY EMR', icon: Plug },
    { id: 'fhir', labelEn: 'FHIR Mapping', labelEs: 'Mapeo FHIR', icon: LinkIcon },
    { id: 'security', labelEn: 'Security', labelEs: 'Seguridad', icon: ShieldAlert },
    { id: 'changelog', labelEn: 'Changelog', labelEs: 'Changelog', icon: Clock },
];

const DevPortal: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [isValidating, setIsValidating] = useState(true);
    const [isValid, setIsValid] = useState(false);
    const [tokenLabel, setTokenLabel] = useState<string>('');
    const [activeSection, setActiveSection] = useState<SectionId>('overview');
    const [language, setLanguage] = useState<Language>('en');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobileMenuOpen]);

    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setIsValidating(false);
                return;
            }

            if (token === 'admin_preview') {
                const adminCtxRaw = localStorage.getItem('hospitalstatus_admin_ctx');
                const authDataRaw = localStorage.getItem('hospitalstatus_auth');
                let hasAccess = false;
                let userName = 'Admin';
                
                try {
                    if (adminCtxRaw) {
                        const ctx = JSON.parse(adminCtxRaw);
                        if (['SUPER_ADMIN', 'GROUP_ADMIN'].includes(ctx.role)) {
                            hasAccess = true;
                            if (ctx.name) {
                                userName = `${ctx.name} (${ctx.role === 'SUPER_ADMIN' ? 'Super Admin' : 'Admin Corporativo'})`;
                            }
                        }
                    }
                    if (!hasAccess && authDataRaw) {
                        const auth = JSON.parse(authDataRaw);
                        const role = auth.role || auth.staffRole;
                        if (['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN', 'FINANCIERO'].includes(role)) {
                            hasAccess = true;
                            const name = auth.staffName || auth.name || 'Admin Local';
                            const roleLabel = role === 'HOSPITAL_ADMIN' ? 'Admin Hospital' : role === 'FINANCIERO' ? 'Finanzas' : 'Admin';
                            userName = `${name} (${roleLabel})`;
                        }
                    }
                } catch (e) {
                    console.error("Error parsing auth data in DevPortal", e);
                }

                if (hasAccess) {
                    setIsValid(true);
                    setTokenLabel(userName);
                    setIsValidating(false);
                } else {
                    setIsValid(false);
                    setIsValidating(false);
                }
                return;
            }

            try {
                // Determine base URL depending on environment
                const baseUrl = window.location.hostname === 'localhost' 
                    ? 'http://localhost:8787' 
                    : '';
                
                const res = await apiFetch(`${baseUrl}/api/docs/validate/${token}`);
                const data = await res.json();
                
                if (res.ok && data.valid) {
                    setIsValid(true);
                    setTokenLabel(data.label);
                } else {
                    setIsValid(false);
                }
            } catch (error) {
                console.error("Token validation error:", error);
                setIsValid(false);
            } finally {
                setIsValidating(false);
            }
        };

        validateToken();
    }, [token]);

    if (isValidating) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center font-sans">
                <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mb-4" />
                <p className="text-slate-500 font-medium">Validating access token...</p>
            </div>
        );
    }

    if (!isValid) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 font-sans">
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <ShieldAlert className="w-8 h-8 text-red-500" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
                    <p className="text-slate-600 mb-6">
                        The documentation access token is invalid, expired, or has been revoked by the administrator.
                    </p>
                    <button 
                        onClick={() => window.location.href = '/'}
                        className="bg-slate-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-colors"
                    >
                        Return to Home
                    </button>
                </div>
            </div>
        );
    }

    const renderContent = () => {
        switch (activeSection) {
            case 'overview': return <OverviewSection lang={language} />;
            case 'api': return <ApiSection lang={language} />;
            case 'auth': return <AuthSection lang={language} />;
            case 'workflow': return <WorkflowSection lang={language} />;
            case 'tasy': return <TasyGuideSection lang={language} />;
            case 'fhir': return <FhirSection lang={language} />;
            case 'security': return <SecuritySection lang={language} />;
            case 'changelog': return <ChangelogSection lang={language} />;
            default: return <OverviewSection lang={language} />;
        }
    };

    return (
        <div className="min-h-screen bg-white font-sans text-slate-900 flex flex-col md:flex-row">
            
            {/* Mobile Header */}
            <div className="md:hidden flex items-center justify-between p-4 border-b border-slate-100 bg-white sticky top-0 z-50">
                <div className="flex items-center gap-2">
                    <img src="/Finales/Hospital Status Logo extendido.png" alt="Hospital Status" className="h-6 object-contain" />
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest pl-2 border-l border-slate-200">Docs</span>
                </div>
                <button 
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="p-2 -mr-2 text-slate-600 focus:outline-none"
                >
                    {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {/* Sidebar Navigation */}
            <aside className={`
                fixed md:sticky inset-0 md:inset-auto md:top-0 md:left-0 h-[100dvh] md:h-screen w-full md:w-64 bg-[#FAFAFA] border-r border-slate-200 z-50 md:z-40
                transform transition-transform duration-300 ease-in-out flex flex-col overflow-y-auto
                ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            `}>
                {/* Desktop Sidebar Header */}
                <div className="p-6 hidden md:block shrink-0">
                    <img src="/Finales/Hospital Status Logo extendido.png" alt="Hospital Status" className="h-7 object-contain mb-2" />
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Developer System</p>
                </div>

                {/* Mobile Sidebar Header */}
                <div className="p-4 flex items-center justify-between md:hidden border-b border-slate-200 bg-[#FAFAFA] shrink-0">
                    <div className="flex items-center gap-2">
                        <img src="/Finales/Hospital Status Logo extendido.png" alt="Hospital Status" className="h-6 object-contain" />
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-widest pl-2 border-l border-slate-200">Menu</span>
                    </div>
                    <button 
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="p-2 -mr-2 text-slate-600 focus:outline-none bg-slate-200/50 rounded-md hover:bg-slate-200"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-4 pb-4 pt-6 md:pt-4 flex-grow">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 px-2">
                        {language === 'en' ? 'Documentation' : 'Documentación'}
                    </p>
                    <nav className="space-y-1">
                        {SECTIONS.map((section) => {
                            const Icon = section.icon;
                            const isActive = activeSection === section.id;
                            return (
                                <button
                                    key={section.id}
                                    onClick={() => {
                                        setActiveSection(section.id);
                                        setIsMobileMenuOpen(false);
                                    }}
                                    className={`
                                        w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                                        ${isActive 
                                            ? 'bg-indigo-50 text-indigo-700' 
                                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                                        }
                                    `}
                                >
                                    <Icon className={`w-4 h-4 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} />
                                    {language === 'en' ? section.labelEn : section.labelEs}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                <div className="p-4 border-t border-slate-200 mt-auto bg-[#FAFAFA] shrink-0 pb-12 md:pb-4">
                    <div className="flex items-center justify-between mb-4 px-2">
                        <span className="text-xs text-slate-500 font-medium">Language / Idioma</span>
                        <div className="flex bg-slate-200 rounded-md p-0.5">
                            <button 
                                onClick={() => setLanguage('en')}
                                className={`px-2 py-1 text-xs font-bold rounded-sm transition-colors ${language === 'en' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                EN
                            </button>
                            <button 
                                onClick={() => setLanguage('es')}
                                className={`px-2 py-1 text-xs font-bold rounded-sm transition-colors ${language === 'es' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                ES
                            </button>
                        </div>
                    </div>
                    
                    <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-widest mb-0.5">Secure Access</p>
                            <p className="text-xs text-emerald-700 line-clamp-2" title={tokenLabel}>
                                {tokenLabel}
                            </p>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 min-w-0 bg-white">
                <div className="max-w-4xl mx-auto p-6 md:p-12 pb-24 lg:p-16">
                    {renderContent()}
                </div>
            </main>

        </div>
    );
};

export default DevPortal;
